import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import isEmpty from "lodash/isEmpty";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { useQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";

import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";
import Page from "../../components/Page";
import useTableBulkScrubber from "../../components/TablePro/extensions/useTableBulkScrubber";
import WarehouseTable from "../../views/warehouse/WarehouseTable";
import {
  WAREHOUSE_INFO,
  WAREHOUSE_LOCATIONS_COLLECTION,
  WAREHOUSE_LOCATIONS_DETAILS,
} from "../../constants/graphql/queries";
import {
  WAREHOUSE_LOCATIONS_BULK_UPDATE_ACTIVE,
  WAREHOUSE_LOCATIONS_EXPORT_EXCEL,
} from "../../constants/graphql/mutations";
import WarehouseFloorPlan from "./WarehouseFloorPlan";
import WarehouseHeader from "./header/WarehouseHeader";
import { formatLocations } from "./utils";
import useQueryParams from "../../hooks/useQueryParams";
import useWarehouseFilters from "./useWarehouseFilters";
import useAuth from "../../hooks/useAuth";
import { useBanner } from "../../contexts/banner";
import initialSettings from "../../constants/tablePro/warehouse";

const WarehouseContext = React.createContext(null);

export const useWarehouseContext = () => {
  const context = React.useContext(WarehouseContext);
  if (!context) {
    throw new Error("useWarehouseContext must be used within a useWarehouseContext");
  }
  return context;
};

const resolveSetterValue = (nextOrUpdater, current) =>
  typeof nextOrUpdater === "function" ? nextOrUpdater(current) : nextOrUpdater;

const getLocationSelectionKey = entity => `${entity.__typename}:${entity.id}`;

// Location types that also exist as mobile locations. Filtering by one of these
// lists mobile rows, so header counts must include them, as they do for search.
const MOBILE_LOCATION_TYPES = ["vault", "pallet", "trailer"];

const splitSelectedLocationKeys = selectedKeys =>
  selectedKeys.reduce(
    (acc, key) => {
      const separatorIndex = key.indexOf(":");
      if (separatorIndex === -1) return acc;

      const typename = key.slice(0, separatorIndex);
      const id = key.slice(separatorIndex + 1);

      if (typename === "MobileWarehouseLocation") {
        acc.mobileWarehouseLocationIds.push(id);
      } else if (typename === "WarehouseLocation") {
        acc.warehouseLocationIds.push(id);
      }
      return acc;
    },
    { warehouseLocationIds: [], mobileWarehouseLocationIds: [] }
  );

export default function Warehouse({ id, view, modal, changeWarehouse, changeView, excludeType }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { displayBanner } = useBanner();
  const [bulkUpdateActive, { loading: bulkUpdating }] = useMutation(
    WAREHOUSE_LOCATIONS_BULK_UPDATE_ACTIVE
  );
  const [exportLocationsExcel, { loading: exportingExcel }] = useMutation(
    WAREHOUSE_LOCATIONS_EXPORT_EXCEL
  );
  const queryParams = useQueryParams();

  // When rendered inside a modal, use local state instead of URL params
  // to avoid changing the URL (which closes the modal).
  const [modalSearch, setModalSearch] = useState("");
  const [modalPage, setModalPage] = useState(1);

  const search = modal ? modalSearch : queryParams.get("wq") || "";
  const page = modal ? modalPage : parseInt(queryParams.get("page") || "1", 10);

  const warehouseTableSettings = useMemo(
    () =>
      isEmpty(user.warehouseTableSettings) ||
      user?.warehouseTableSettings?.version !== initialSettings.version
        ? initialSettings
        : user.warehouseTableSettings,
    [user.warehouseTableSettings]
  );

  const {
    height: queryHeight,
    occupied: queryOccupied,
    locationTypes: queryLocationTypes,
    active: queryActive,
    setHeight: setQueryHeight,
    setOccupied: setQueryOccupied,
    setLocationTypes: setQueryLocationTypes,
    setActive: setQueryActive,
    clearFilters: clearQueryFilters,
  } = useWarehouseFilters();

  const [modalHeight, setModalHeight] = useState(null);
  const [modalOccupied, setModalOccupied] = useState(null);
  const [modalLocationTypes, setModalLocationTypes] = useState([]);
  const [modalActive, setModalActive] = useState(true);

  const height = modal ? modalHeight : queryHeight;
  const occupied = modal ? modalOccupied : queryOccupied;
  const locationTypes = modal ? modalLocationTypes : queryLocationTypes;
  const active = modal ? modalActive : queryActive;

  const setOccupied = newOccupied => {
    if (!modal) {
      setQueryOccupied(newOccupied);
      return;
    }
    setModalOccupied(current => resolveSetterValue(newOccupied, current));
    setModalPage(1);
  };

  const setHeight = (newHeight, newOccupied) => {
    if (!modal) {
      setQueryHeight(newHeight, newOccupied);
      return;
    }
    setModalHeight(newHeight || null);
    if (newOccupied !== undefined) {
      setModalOccupied(current => resolveSetterValue(newOccupied, current));
    }
    setModalPage(1);
  };

  const setLocationTypes = newLocationTypes => {
    if (!modal) {
      setQueryLocationTypes(newLocationTypes);
      return;
    }
    if (newLocationTypes?.length > 0) {
      setModalLocationTypes([newLocationTypes[0]]);
    } else {
      setModalLocationTypes([]);
    }
    setModalPage(1);
  };

  const setActive = newActive => {
    if (!modal) {
      setQueryActive(newActive);
      return;
    }
    setModalActive(current => resolveSetterValue(newActive, current));
    setModalPage(1);
  };

  const clearFilters = () => {
    if (!modal) {
      clearQueryFilters();
      return;
    }
    setModalHeight(null);
    setModalOccupied(null);
    setModalLocationTypes([]);
    setModalActive(true);
    setModalPage(1);
  };

  const [updating, setUpdating] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [limit] = useState(200);

  const handleSearchChange = newSearch => {
    if (modal) {
      setModalSearch(newSearch || "");
      setModalPage(1);
      return;
    }
    const params = new URLSearchParams(queryParams.toString());
    if (newSearch) {
      params.set("wq", newSearch);
    } else {
      params.delete("wq");
    }
    params.delete("page");
    navigate({ search: params.toString() });
  };

  const { data: { warehouse = {} } = {}, loading: loadingWarehouse } = useQuery(WAREHOUSE_INFO, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { id },
  });
  const variables = useMemo(() => {
    const values = {
      warehouseId: id,
      projectIds: selectedProjects,
      search: search.trim(),
      page,
      limit,
      order: warehouseTableSettings.order,
    };
    if (typeof active === "boolean") {
      values.active = active;
    }
    if (typeof occupied === "boolean") {
      values.occupied = occupied;
    }
    if (height) {
      values.height = height;
    }
    if (locationTypes?.length === 1) {
      values.locationType = locationTypes[0];
    }
    if (excludeType) {
      values.excludeType = excludeType;
    }
    return values;
  }, [
    id,
    selectedProjects,
    search,
    occupied,
    height,
    locationTypes,
    excludeType,
    page,
    limit,
    active,
    warehouseTableSettings.order,
  ]);

  const { data: { warehouseLocationsCollection = {} } = {}, loading, refetch } = useQuery(
    WAREHOUSE_LOCATIONS_COLLECTION,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      variables,
    }
  );
  const { data: { warehouseLocationsDetails = {} } = {} } = useQuery(WAREHOUSE_LOCATIONS_DETAILS, {
    fetchPolicy: "cache-and-network",
    variables: { warehouseId: id },
  });
  const { collection = [], metadata = {} } = warehouseLocationsCollection;
  const list = formatLocations(collection);
  const tableList = useMemo(
    () => collection.map(v => ({ ...v, warehouseId: warehouse?.warehouseId })),
    [collection, warehouse?.warehouseId]
  );
  const visibleSelectionIds = useMemo(
    () => tableList.map(getLocationSelectionKey),
    [tableList]
  );
  const totalSelectionIds = useMemo(() => {
    if (metadata.totalIds?.length) return metadata.totalIds;
    return visibleSelectionIds;
  }, [metadata.totalIds, visibleSelectionIds]);
  const selectionFilterKey = useMemo(
    () =>
      JSON.stringify({
        id,
        search,
        occupied,
        height,
        locationTypes,
        active,
        selectedProjects,
        excludeType,
        order: warehouseTableSettings.order,
      }),
    [
      id,
      search,
      occupied,
      height,
      locationTypes,
      active,
      selectedProjects,
      excludeType,
      warehouseTableSettings.order,
    ]
  );
  const {
    selectedIds,
    unSelectBulk,
    props: bulkSelectionProps,
  } = useTableBulkScrubber(totalSelectionIds, {
    getEntityId: getLocationSelectionKey,
    orderedIds: visibleSelectionIds,
  });
  const hasSelection = selectedIds.length > 0;
  const canActivateSelected = hasSelection;
  const canDeactivateSelected = hasSelection;

  useEffect(() => {
    unSelectBulk();
  }, [selectionFilterKey, unSelectBulk]);

  const Wrapper = useMemo(() => (modal ? Box : props => <Page sx={{ pt: 0 }} {...props} />), [
    modal,
  ]);

  const handleRefetch = useCallback(() => {
    setUpdating(true);
    refetch().then(() => setUpdating(false));
  }, [refetch]);

  const handleBulkActiveUpdate = useCallback(
    async nextActive => {
      const { warehouseLocationIds, mobileWarehouseLocationIds } = splitSelectedLocationKeys(
        selectedIds
      );
      const selectedCount = warehouseLocationIds.length + mobileWarehouseLocationIds.length;

      if (selectedCount === 0) return;

      let mutationFailure = null;

      const response = await bulkUpdateActive({
        variables: {
          warehouseLocationIds,
          mobileWarehouseLocationIds,
          active: nextActive,
        },
        onFailure: errors => {
          mutationFailure = errors;
          notifyError(errors);
        },
      });

      const payload = response?.data?.warehouseLocationsBulkUpdateActive;
      const errors = payload?.errors || [];

      if (mutationFailure) return;

      if (errors.length > 0) {
        notifyError(errors.map(error => error.message).join(", "));
        return;
      }

      notifySuccess(
        `${payload?.successCount || selectedCount} location${
          selectedCount === 1 ? "" : "s"
        } ${nextActive ? "activated" : "deactivated"}.`
      );
      unSelectBulk();
      handleRefetch();
    },
    [bulkUpdateActive, handleRefetch, notifyError, notifySuccess, selectedIds, unSelectBulk]
  );

  const handleExportExcel = useCallback(async () => {
    const { warehouseLocationIds, mobileWarehouseLocationIds } = splitSelectedLocationKeys(
      selectedIds
    );
    const selectedCount = warehouseLocationIds.length + mobileWarehouseLocationIds.length;

    if (selectedCount === 0) return;

    let mutationFailure = null;

    displayBanner({
      message: "Generating file...",
      type: "export-excel",
      startedAt: Date.now(),
    });

    const response = await exportLocationsExcel({
      variables: {
        warehouseLocationIds,
        mobileWarehouseLocationIds,
      },
      onFailure: errors => {
        mutationFailure = errors;
        notifyError(errors);
      },
    });

    const payload = response?.data?.warehouseLocationsExportExcel;
    const errors = payload?.errors || [];

    if (mutationFailure) return;

    if (errors.length > 0 || payload?.success === false) {
      notifyError(
        errors.map(error => error.message).join(", ") || "Unable to export selected locations."
      );
    }
  }, [displayBanner, exportLocationsExcel, notifyError, selectedIds]);

  const includesMobileLocations =
    Boolean(search.trim()) || MOBILE_LOCATION_TYPES.includes(locationTypes?.[0]);

  const bulkActions =
    view === "table" && hasSelection
      ? {
          selectedCount: selectedIds.length,
          canActivateSelected,
          canDeactivateSelected,
          loading: bulkUpdating || exportingExcel,
          onActivate: () => handleBulkActiveUpdate(true),
          onDeactivate: () => handleBulkActiveUpdate(false),
          onExportExcel: handleExportExcel,
        }
      : null;

  const handlePageChange = newPage => {
    if (modal) {
      setModalPage(newPage);
      return;
    }
    const params = new URLSearchParams(queryParams.toString());
    if (newPage > 1) {
      params.set("page", newPage);
    } else {
      params.delete("page");
    }
    navigate({ search: params.toString() });
  };

  return (
    <WarehouseContext.Provider value={{ refetchWarehouse: handleRefetch }}>
      {updating && <LinearProgress />}
      <Wrapper>
        <Paper sx={{ p: 2 }}>
          {loadingWarehouse ? null : (
            <WarehouseHeader
              view={view}
              modal={modal}
              warehouse={warehouse}
              warehouseLocations={collection}
              includesMobileLocations={includesMobileLocations}
              occupiedCount={
                includesMobileLocations ? metadata.occupiedCount : metadata.baseOccupiedCount
              }
              totalCount={includesMobileLocations ? metadata.totalCount : metadata.baseTotalCount}
              warehouseOccupiedCount={warehouseLocationsDetails.totalOccupiedBaseLocations || 0}
              warehouseTotalCount={
                (warehouseLocationsDetails.totalEmptyBaseLocations || 0) +
                (warehouseLocationsDetails.totalOccupiedBaseLocations || 0)
              }
              handleRefetch={handleRefetch}
              selectedProjects={selectedProjects}
              setSelectedProjects={setSelectedProjects}
              search={search}
              changeSearch={handleSearchChange}
              changeWarehouse={changeWarehouse}
              changeView={changeView}
              occupied={occupied}
              active={active}
              setOccupied={setOccupied}
              setActive={setActive}
              height={height}
              setHeight={setHeight}
              locationTypes={locationTypes}
              setLocationTypes={setLocationTypes}
              clearFilters={clearFilters}
              bulkActions={bulkActions}
            />
          )}
        </Paper>
        {loading ? (
          <LinearProgress className="mt-2" />
        ) : (
          <Box mt={2} border="1px solid transparent">
            {view === "table" ? (
              <WarehouseTable
                modal={modal}
                list={tableList}
                refetch={handleRefetch}
                pagination={{
                  page,
                  limit,
                  totalCount: metadata.totalCount,
                  totalPages: metadata.totalPages,
                  onPageChange: handlePageChange,
                }}
                bulkSelectionProps={bulkSelectionProps}
                selectedIds={selectedIds}
                getLocationSelectionKey={getLocationSelectionKey}
              />
            ) : (
              <WarehouseFloorPlan list={list} />
            )}
          </Box>
        )}
      </Wrapper>
    </WarehouseContext.Provider>
  );
}
